import React, { useEffect, useState } from "react";
import { Phone, UserRound, X, ZoomIn } from "lucide-react";
import { ICON_STROKE } from "../../ui/icon";
import { toTitleCase } from "../../../utils/titleCase";

const PublicManagedSubjectHero = ({
  subjectType,
  name,
  photo,
  profileItems = [],
  primaryContact,
  onCallContact,
  t,
}) => {
  const [photoOpen, setPhotoOpen] = useState(false);
  const displayName = name || t("public.medicalInfoTitle");
  const photoAlt = t("public.ownerPhotoAlt").replace("{{name}}", displayName);
  const notAvailable = t("public.notAvailable");

  const verifyText = photo
    ? subjectType === "pet"
      ? t("public.verifyPhotoHintPet")
      : t("public.verifyPhotoHintVehicle")
    : subjectType === "pet"
      ? t("public.verifyNoPhotoHintPet")
      : t("public.verifyNoPhotoHintVehicle");

  useEffect(() => {
    if (!photoOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPhotoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoOpen]);

  return (
    <section
      className="pub-hero pub-hero--managed"
      aria-label={subjectType === "pet" ? "Pet overview" : "Vehicle overview"}
    >
      <div className="pub-hero__main">
        <div className="pub-hero__photo-col pub-hero__photo-col--managed-top">
          {photo ? (
            <button
              type="button"
              className="pub-hero__photo-btn"
              onClick={() => setPhotoOpen(true)}
              aria-label={t("public.tapToEnlarge")}
            >
              <img src={photo} alt={photoAlt} className="pub-hero__photo" />
              <span className="pub-hero__zoom" aria-hidden>
                <ZoomIn size={14} strokeWidth={ICON_STROKE} />
              </span>
            </button>
          ) : (
            <div className="pub-hero__photo pub-hero__photo--placeholder" aria-hidden>
              <UserRound size={40} strokeWidth={1.5} />
              <span>{t("public.noPhoto")}</span>
            </div>
          )}
          <p className="pub-hero__verify pub-hero__verify--below-photo">{verifyText}</p>
        </div>

        <div className="pub-hero__info">
          <h1 className="pub-hero__name">{displayName}</h1>
          {profileItems.length > 0 && (
            <dl className="pub-hero__identity pub-hero__identity--managed">
              {profileItems.map((row) => {
                const value = (row.value || "").trim() || notAvailable;
                const muted = value === notAvailable;
                return (
                  <div key={row.id || row.key} className="pub-hero__fact pub-hero__fact--wide">
                    <dt>{row.key}</dt>
                    <dd className={muted ? "pub-hero__fact-value--muted" : undefined}>{value}</dd>
                  </div>
                );
              })}
            </dl>
          )}
          {primaryContact ? (
            <button
              type="button"
              className="pub-hero__call-btn pub-hero__call-btn--block"
              onClick={() => onCallContact?.(primaryContact)}
              aria-label={`${t("public.callPrimary")} — ${toTitleCase(primaryContact.name)}`}
            >
              <Phone size={16} strokeWidth={ICON_STROKE} aria-hidden />
              <span>
                {t("public.callPrimary")}
                {primaryContact.name ? ` (${toTitleCase(primaryContact.name)})` : ""}
              </span>
            </button>
          ) : (
            <span className="pub-hero__contact-name">{t("public.noPrimaryContactShort")}</span>
          )}
        </div>
      </div>

      {photoOpen && (
        <div
          className="pub-photo-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={photoAlt}
          onClick={() => setPhotoOpen(false)}
        >
          <button
            type="button"
            className="pub-photo-viewer__close"
            onClick={() => setPhotoOpen(false)}
            aria-label={t("public.closePhoto")}
          >
            <X size={24} />
          </button>
          <img src={photo} alt={photoAlt} className="pub-photo-viewer__img" />
          <p className="pub-photo-viewer__name">{displayName}</p>
        </div>
      )}
    </section>
  );
};

export default PublicManagedSubjectHero;
